<template>
  <component
    :is="tag"
    v-bind="linkAttrs"
    @click="onClick">
    <slot />
  </component>
</template>

<script setup>
import { computed, useAttrs } from 'vue'
import { isRoutable } from '@/router/instance'
import { logUiEvent } from '@/common/activityLog'

// Renders a router-link when the href is handled by the client-side router,
// a regular full-page-load anchor otherwise (external links, downloads,
// target=_blank, pages that haven't opted in, or no router mounted at all).
// The dynamic `:is` string is deliberate: importing RouterLink here would
// pull vue-router into the main bundle every legacy page downloads, and a
// static <router-link> in the template would trigger resolveComponent (and
// its "failed to resolve" warning) even on pages without a router.
const props = defineProps({
  href: { type: String, required: true }
})

const attrs = useAttrs()

const routable = computed(() => {
  // rails-ujs data-method links must stay real anchors — router-link would
  // navigate instead of letting ujs issue the non-GET request.
  if (attrs.target || attrs['data-method']) return false
  if (!props.href.startsWith('/')) return false

  return isRoutable(props.href)
})

const tag = computed(() => (routable.value ? 'RouterLink' : 'a'))
const linkAttrs = computed(() => (routable.value ? { to: props.href } : { href: props.href }))

// Same-origin clicks become page views. Only a link to another origin is an interaction.
const onClick = () => {
  const url = new URL(props.href, window.location.href)
  if (!url.host || url.origin === window.location.origin) return

  logUiEvent('link_click', { target: url.host })
}
</script>
